/* MIT License

Copyright (c) 2025 Brad Schick

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE. */

import { getSodium } from './crypto';
import { base64URLStringToBuffer, bufferToBase64URLString } from './base64';

let logErrors = true;

export function setLogErrors(enabled: boolean): void {
   logErrors = enabled;
}

export function logError(err: unknown): void {
   if (logErrors) {
      console.error(err);
   }
}

export function hasArrayBuffer(value: Uint8Array): value is Uint8Array<ArrayBuffer> {
   return value.buffer instanceof ArrayBuffer;
}

export function ensureArrayBuffer(value: Uint8Array): Uint8Array<ArrayBuffer> {
   if (value.buffer instanceof ArrayBuffer) {
      return value as Uint8Array<ArrayBuffer>;
   } else {
      return new Uint8Array(value);
   }
}

export function getArrayBuffer(value: ArrayBufferView | ArrayBuffer | number[]): ArrayBuffer {
   if (value instanceof ArrayBuffer) {
      return value;
   }
   if (Array.isArray(value)) {
      return new Uint8Array(value).buffer;
   }

   const buffer = value.buffer;
   if (!(buffer instanceof ArrayBuffer)) {
      // Handles SharedArrayBuffer or other buffers that aren't standard ArrayBuffers.
      const bytes = new Uint8Array(buffer, value.byteOffset, value.byteLength);
      const temp = new Uint8Array(bytes.byteLength);
      temp.set(bytes);
      return temp.buffer;
   }
   if (value.byteOffset !== 0 || value.byteLength !== buffer.byteLength) {
      // Slices the underlying ArrayBuffer directly. Avoids intermediates and works with Node.js Buffer.
      return buffer.slice(value.byteOffset, value.byteOffset + value.byteLength);
   }

   return buffer;
}

// Returns base64Url text
export function bytesToBase64(bytes: Uint8Array): string {
   return bufferToBase64URLString(getArrayBuffer(bytes));
}

// Accepts either base64 or base64Url text
export function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
   return new Uint8Array(base64URLStringToBuffer(b64));
}

export function bufferToHexString(buffer: ArrayBuffer): string {
   const bytes = new Uint8Array(buffer);
   let str = '';

   for (let i = 0; i < bytes.length; i++) {
      // Convert the byte to its hexadecimal representation and pad to 2 digits
      str += bytes[i].toString(16).padStart(2, '0');
   }
   return str;
}

/* Javascript converts to signed 32 bit int when using bit shifting
   and masking, so do this instead. Count is the number of bytes
   used to pack the number.  */
export function numToBytes(num: number, count: number): Uint8Array<ArrayBuffer> {
   if (count < 1 || num >= 256 ** count) {
      throw new Error(`Invalid arguments ${count} for ${num}`);
   }
   const arr = new Uint8Array(count);
   for (let i = 0; i < count; ++i) {
      arr[i] = num % 256;
      num = Math.floor(num / 256);
   }
   return arr;
}

export function bytesToNum(arr: Uint8Array): number {
   let num = 0;
   for (let i = arr.length - 1; i >= 0; --i) {
      num = num * 256 + arr[i];
   }
   return num;
}

export function clamp(n: number, min: number, max: number): number {
   return Math.min(max, Math.max(min, n));
}

export function concatArrays(arrays: Uint8Array[]): Uint8Array<ArrayBuffer> {
   if (arrays.length === 0) {
      throw new Error('No arrays to concat');
   }
   if (arrays.length === 1) {
      return ensureArrayBuffer(arrays[0]);
   }

   const totalLen = arrays.reduce((sum, part) => sum + part.byteLength, 0);
   const output = new Uint8Array(totalLen);
   let offset = 0;
   for (const array of arrays) {
      output.set(array, offset);
      offset += array.byteLength;
   }
   return output;
}

export function byteCount(num: number): number {
   if (num < 0) {
      throw new Error('Value must be an unsigned integer (non-negative).');
   }
   if (num === 0) {
      return 1;
   }
   return Math.ceil(Math.log2(num + 1) / 8);
}

export function browserSupportsFilePickers(): boolean {
   //@ts-expect-error
   if (window.showSaveFilePicker) {
      return true;
   } else {
      return false;
   }
}

export function browserSupportsBytesStream(): boolean {
   try {
      new ReadableStream({ type: 'bytes' });
      return true;
   } catch {
      return false;
   }
}

export function makeTookMsg(start: number, end: number, word: string = 'took'): string {
   const millis = end - start;
   const minutes = millis / 60000;
   const seconds = millis / 1000;
   if (minutes >= 1.1) {
      return `${word} ${Math.round(minutes * 10) / 10} minutes`;
   } else if (seconds >= 2) {
      return `${word} ${Math.round(seconds)} seconds`;
   }
   return `${word} ${millis} millis`;
}

// important to note that a missing key is considered expired
export function expired(storage: Storage, key: string): boolean {
   const value = storage.getItem(key);
   if (!value) {
      return true;
   }
   const expiry = Date.parse(value);
   return !Number.isFinite(expiry) || Date.now() > expiry;
}

export class ProcessCancelled extends Error {
   constructor() {
      super('Process Cancelled');
      this.name = this.constructor.name;
   }

   static isProcessCancelled(err: unknown): boolean {
      return err instanceof ProcessCancelled || (err instanceof Error && err.message.indexOf('ProcessCancelled') >= 0);
   }
}

export function streamFromBytes(data: Uint8Array<ArrayBuffer>): ReadableStream<Uint8Array> {
   const blob = new Blob([data], { type: 'application/octet-stream' });
   return blob.stream();
}

export function streamFromBase64(b64: string): ReadableStream<Uint8Array> {
   return streamFromBytes(base64ToBytes(b64));
}

export class BYOBStreamReader {
   private _reader: ReadableStreamBYOBReader | ReadableStreamDefaultReader;
   private _extra?: Uint8Array<ArrayBuffer>;
   private _byob: boolean;

   constructor(stream: ReadableStream<Uint8Array>) {
      try {
         this._reader = stream.getReader({ mode: 'byob' });
         this._byob = true;
      } catch {
         this._byob = false;
         this._reader = stream.getReader();
      }
   }

   cleanup() {
      this._reader.releaseLock();
   }

   // Use when the reader cannot accept less then the size of output (or stream done)
   async readFill(buffer: ArrayBuffer): Promise<[data: Uint8Array<ArrayBuffer>, done: boolean]> {
      try {
         if (this._byob) {
            return this._readBYOB(buffer, false);
         } else {
            return this._readStupidSafari(buffer, false);
         }
      } catch (err) {
         this.cleanup();
         throw err;
      }
   }

   async readAvailable(buffer: ArrayBuffer): Promise<[data: Uint8Array<ArrayBuffer>, done: boolean]> {
      try {
         if (this._byob) {
            return this._readBYOB(buffer, true);
         } else {
            return this._readStupidSafari(buffer, true);
         }
      } catch (err) {
         this.cleanup();
         throw err;
      }
   }

   private async _readBYOB(
      buffer: ArrayBuffer,
      breakOnStall: boolean,
   ): Promise<[data: Uint8Array<ArrayBuffer>, done: boolean]> {
      const reader = this._reader as ReadableStreamBYOBReader;

      const targetBytes = buffer.byteLength;
      let readBytes = 0;
      let streamDone = false;

      while (readBytes < targetBytes) {
         const { done, value } = await reader.read(new Uint8Array(buffer, readBytes, targetBytes - readBytes));

         if (value) {
            readBytes += value.byteLength;
            buffer = value.buffer;
         }
         streamDone = done;
         if (done || (breakOnStall && !value?.byteLength)) {
            break;
         }
      }

      return [new Uint8Array(buffer, 0, readBytes), streamDone];
   }

   private async _readStupidSafari(
      buffer: ArrayBuffer,
      breakOnStall: boolean,
   ): Promise<[data: Uint8Array<ArrayBuffer>, done: boolean]> {
      const reader = this._reader as ReadableStreamDefaultReader;

      const targetBytes = buffer.byteLength;
      const writeable = new Uint8Array(buffer);
      let wroteBytes = 0;
      let streamDone = false;

      while (wroteBytes < targetBytes) {
         let done: boolean;
         let value: Uint8Array<ArrayBuffer>;

         if (this._extra) {
            done = false;
            value = this._extra;
            this._extra = undefined;
         } else {
            const results = await reader.read();
            done = results.done;
            if (typeof results.value === 'string') {
               value = new TextEncoder().encode(results.value);
            } else {
               value = results.value;
            }
         }

         if (value) {
            const unfilledBytes = targetBytes - wroteBytes;
            if (value.byteLength > unfilledBytes) {
               this._extra = value.subarray(unfilledBytes);
               value = value.subarray(0, unfilledBytes);
               done = false;
            }

            writeable.set(value, wroteBytes);
            wroteBytes += value.byteLength;
         }

         streamDone = done;
         if (done || (breakOnStall && !value?.byteLength)) {
            break;
         }
      }

      return [new Uint8Array(buffer, 0, wroteBytes), streamDone];
   }
}

type TrueType = true;
type FalseType = false;

export async function readStreamAll(stream: ReadableStream<Uint8Array>): Promise<Uint8Array<ArrayBuffer>>;
export async function readStreamAll(
   stream: ReadableStream<Uint8Array>,
   decode: FalseType,
): Promise<Uint8Array<ArrayBuffer>>;
export async function readStreamAll(stream: ReadableStream<Uint8Array>, decode: TrueType): Promise<string>;
export async function readStreamAll(
   stream: ReadableStream<Uint8Array>,
   decode: TrueType | FalseType = false,
): Promise<string | Uint8Array<ArrayBuffer>> {
   let result: string | Uint8Array<ArrayBuffer>;
   const reader = stream.getReader();
   try {
      const blocks: Uint8Array[] = [];

      while (true) {
         const { done, value } = await reader.read();
         if (value) {
            blocks.push(value);
         }
         if (done) {
            break;
         }
      }

      result = concatArrays(blocks);
      if (decode) {
         result = new TextDecoder().decode(result);
      }
   } finally {
      reader.releaseLock();
   }

   return result;
}

export function streamWriteBYOD(
   controller: ReadableByteStreamController | ReadableStreamDefaultController<Uint8Array>,
   data: Uint8Array,
): number {
   let written = 0;

   if (
      !browserSupportsBytesStream() ||
      controller instanceof ReadableStreamDefaultController ||
      !controller.byobRequest?.view
   ) {
      written += data.byteLength;
      controller.enqueue(ensureArrayBuffer(data));
   } else {
      const byodView = controller.byobRequest.view;
      const byodBytes = Math.min(data.byteLength, byodView.byteLength);
      const writeableView = new Uint8Array(byodView.buffer, byodView.byteOffset, byodView.byteLength);
      writeableView.set(data.subarray(0, byodBytes));
      written += byodBytes;
      controller.byobRequest.respond(byodBytes);

      if (byodBytes < data.byteLength) {
         const remainder = data.subarray(byodBytes);
         written += remainder.byteLength;
         controller.enqueue(ensureArrayBuffer(remainder));
      }
   }

   return written;
}

export async function selectCipherFile(): Promise<FileSystemFileHandle> {
   try {
      //@ts-expect-error
      const [fileHandle] = await window.showOpenFilePicker({
         id: 'quickcrypt_org',
         multiple: false,
         types: [
            {
               description: 'Encrypted files',
               accept: {
                  'application/octet-stream': ['.qq', '.json'],
               },
            },
            {
               description: 'JSON file',
               accept: {
                  'application/json': ['.json'],
               },
            },
         ],
      });
      return fileHandle;
   } catch (err) {
      logError(err);
      throw new ProcessCancelled();
   }
}

export async function selectClearFile(): Promise<FileSystemFileHandle> {
   try {
      //@ts-expect-error
      const [fileHandle] = await window.showOpenFilePicker({
         id: 'quickcrypt_org',
         multiple: false,
      });
      return fileHandle;
   } catch (err) {
      logError(err);
      throw new ProcessCancelled();
   }
}

// Subset of the File System Access API's SaveFilePickerOptions, which the TS DOM lib does not declare.
interface SaveFilePickerOptions {
   id: string;
   suggestedName: string;
   types?: {
      description: string;
      accept: Record<string, string[]>;
   }[];
}

async function selectWriteableFileImpl(options: SaveFilePickerOptions): Promise<FileSystemFileHandle> {
   try {
      //@ts-expect-error
      return await window.showSaveFilePicker(options);
   } catch (err) {
      logError(err);
      throw new ProcessCancelled();
   }
}

export async function selectWriteableFile(baseName?: string): Promise<FileSystemFileHandle> {
   const suggested = baseName ?? '';
   const options = {
      id: 'quickcrypt_org',
      suggestedName: suggested,
   };

   return selectWriteableFileImpl(options);
}

export async function selectWriteableJsonFile(baseName?: string): Promise<FileSystemFileHandle> {
   const suggested = baseName ? `${baseName}.json` : '';
   const options = {
      id: 'quickcrypt_org_json',
      suggestedName: suggested,
      types: [
         {
            description: 'JSON file',
            accept: {
               'application/json': ['.json'],
            },
         },
      ],
   };

   return selectWriteableFileImpl(options);
}

export async function selectWriteableQQFile(baseName?: string): Promise<FileSystemFileHandle> {
   const suggested = baseName ? `${baseName}.qq` : '';
   const options = {
      id: 'quickcrypt_org_qq',
      suggestedName: suggested,
      types: [
         {
            description: 'Encrypted files',
            accept: {
               'application/octet-stream': ['.qq'],
            },
         },
      ],
   };

   return selectWriteableFileImpl(options);
}

export async function selectWriteableTxtFile(baseName?: string): Promise<FileSystemFileHandle> {
   const suggested = baseName ? `${baseName}.txt` : '';
   const options = {
      id: 'quickcrypt_org_txt',
      suggestedName: suggested,
      types: [
         {
            description: 'Text file',
            accept: {
               'text/plain': ['.txt'],
            },
         },
      ],
   };

   return selectWriteableFileImpl(options);
}

export function getRandom(length: number): Uint8Array<ArrayBuffer> {
   return ensureArrayBuffer(getSodium().randombytes_buf(length));
}

// Helper function to get bytes from a string, truncated to a maximum byte length, ensuring valid UTF-8
export function bytesFromUTF8String(str: string, maxByteLength: number): Uint8Array<ArrayBuffer> {
   const encoder = new TextEncoder();

   // Attempt to encode the entire string first
   const fullEncodedBytes = encoder.encode(str);
   if (fullEncodedBytes.byteLength <= maxByteLength) {
      return fullEncodedBytes;
   }

   // If too long, fall back to character-by-character truncation using pre-allocation
   // POTENTIAL BUG: Javascript has the annoying habbit of encode single character
   // different than sequence of characters because a sequence can have
   // "Zero Width Joiner ZWJ" chars added while a single unicode point will not
   const preallocatedBuffer = new Uint8Array(maxByteLength);
   let bytesWritten = 0;
   for (const char of str) {
      const charBytes = encoder.encode(char);
      if (bytesWritten + charBytes.byteLength <= maxByteLength) {
         preallocatedBuffer.set(charBytes, bytesWritten);
         bytesWritten += charBytes.byteLength;
      } else {
         break;
      }
   }
   // Return a slice of the buffer containing only the bytes written
   return preallocatedBuffer.slice(0, bytesWritten);
}
